/**
 * Firebase phone auth for group invite flow
 * Phase 3b: Real Firebase Authentication
 *
 * Uses module-level state to store the pending confirmation object.
 * This avoids passing non-cloneable objects through React Router's
 * history.push state (which uses the History API's pushState).
 */
import firebase from '../../services/firebase';

const { auth } = firebase;

// ─── Module-level state ───────────────────────────────────────────────────────
// Stores the pending confirmation and phone between screens.
// This is the standard pattern for multi-page OTP flows.

let pendingConfirmation = null;
let pendingPhone = null;
let pendingDisplayPhone = null;

/**
 * Get the pending phone number (for display on verify screen)
 */
export function getPendingPhone() {
  return pendingPhone;
}

/**
 * Get the pending display phone (formatted for UI)
 */
export function getPendingDisplayPhone() {
  return pendingDisplayPhone;
}

/**
 * Clear all pending state (call after successful verify or on abandon)
 */
export function clearPending() {
  pendingConfirmation = null;
  pendingPhone = null;
  pendingDisplayPhone = null;
}

/**
 * Check if there's a pending verification
 */
export function hasPendingVerification() {
  return pendingConfirmation !== null;
}

// ─── Error mapping ────────────────────────────────────────────────────────────

const SEND_CODE_ERRORS = {
  'auth/operation-not-allowed': "Sign-in by phone isn't available right now.",
  'auth/invalid-phone-number': "That phone number doesn't look right.",
  'auth/too-many-requests': 'Too many tries. Wait a minute and try again.',
  'auth/quota-exceeded': 'Splyt is rate-limited. Try again in a few minutes.',
  'auth/captcha-check-failed': "Couldn't verify you're human. Refresh and try again.",
  'auth/network-request-failed': "Couldn't reach Splyt. Check your connection.",
  'auth/missing-phone-number': "That phone number doesn't look right.",
};

const VERIFY_CODE_ERRORS = {
  'auth/invalid-verification-code': "That code didn't match. Try again.",
  'auth/code-expired': 'Code expired. Tap "Send a new code".',
  'auth/session-expired': 'Session expired. Please request a new code.',
  'auth/invalid-verification-id': 'Session expired. Please request a new code.',
};

/**
 * Check if an error is a Firebase auth error
 */
function isAuthError(err) {
  return (
    err &&
    typeof err === 'object' &&
    'code' in err &&
    typeof err.code === 'string'
  );
}

// ─── RecaptchaVerifier ────────────────────────────────────────────────────────

/**
 * Create a RecaptchaVerifier for the given container ID
 * @param {string} containerId - DOM element ID for reCAPTCHA
 * @returns {firebase.auth.RecaptchaVerifier}
 */
export function createRecaptchaVerifier(containerId) {
  return new firebase.auth.RecaptchaVerifier(containerId, {
    size: 'invisible',
  });
}

/**
 * Reset the reCAPTCHA widget for resending codes
 * @param {firebase.auth.RecaptchaVerifier} recaptchaVerifier
 */
export async function resetRecaptcha(recaptchaVerifier) {
  if (!recaptchaVerifier) return;

  try {
    const widgetId = await recaptchaVerifier.render();
    if (window.grecaptcha) {
      window.grecaptcha.reset(widgetId);
    }
  } catch (err) {
    // Render may have already been called - that's OK
    console.warn('[firebaseAuth] resetRecaptcha warning:', err);
  }
}

// ─── Auth functions ───────────────────────────────────────────────────────────

/**
 * Send a verification code to the given phone number.
 * Stores the confirmation in module state for later verification.
 *
 * @param {string} phone - Phone number in E.164 format (e.g., +15555550934)
 * @param {firebase.auth.RecaptchaVerifier} recaptchaVerifier
 * @param {Object} options - Additional options
 * @param {string} options.displayPhone - Formatted phone for display
 * @returns {Promise<{success: boolean}>}
 * @throws {Error} With user-friendly message on failure
 */
export async function sendCode(phone, recaptchaVerifier, options = {}) {
  if (!recaptchaVerifier) {
    throw new Error("Couldn't verify you're human. Refresh and try again.");
  }

  try {
    auth().useDeviceLanguage();
    const confirmation = await auth().signInWithPhoneNumber(phone, recaptchaVerifier);

    // Store in module state
    pendingConfirmation = confirmation;
    pendingPhone = phone;
    pendingDisplayPhone = options.displayPhone || phone;

    return { success: true };
  } catch (err) {
    console.error('[firebaseAuth] sendCode error:', err);

    // Map to friendly message
    let message = "Couldn't reach Splyt. Check your connection.";
    if (isAuthError(err) && SEND_CODE_ERRORS[err.code]) {
      message = SEND_CODE_ERRORS[err.code];
    }

    const error = new Error(message);
    error.code = err?.code;
    throw error;
  }
}

/**
 * Verify the SMS code using the stored confirmation.
 *
 * @param {string} code - 6-digit code entered by user
 * @returns {Promise<{user: firebase.User, isNewUser: boolean, matchedSlot: null}>}
 *   isNewUser is Firebase's additionalUserInfo.isNewUser: true on the first ever
 *   verification for this phone number (account_created), false when returning.
 * @throws {Error} With user-friendly message on failure
 */
export async function verifyCode(code) {
  if (!pendingConfirmation) {
    throw new Error('No pending verification. Please start over.');
  }

  try {
    const credential = await pendingConfirmation.confirm(code);
    return {
      user: credential.user,
      isNewUser: credential.additionalUserInfo?.isNewUser === true,
      matchedSlot: null, // Phase 5 will wire this up
    };
  } catch (err) {
    console.error('[firebaseAuth] verifyCode error:', err);

    // Map to friendly message
    let message = "That code didn't match. Try again.";
    if (isAuthError(err) && VERIFY_CODE_ERRORS[err.code]) {
      message = VERIFY_CODE_ERRORS[err.code];
    }

    const error = new Error(message);
    error.code = err?.code;
    throw error;
  }
}

/**
 * Resend the verification code.
 * Uses the stored phone number and updates the confirmation.
 *
 * @param {firebase.auth.RecaptchaVerifier} recaptchaVerifier
 * @returns {Promise<{success: boolean}>}
 */
export async function resendCode(recaptchaVerifier) {
  if (!pendingPhone) {
    throw new Error('No pending verification. Please start over.');
  }

  // Reset reCAPTCHA before resending
  await resetRecaptcha(recaptchaVerifier);

  try {
    auth().useDeviceLanguage();
    const confirmation = await auth().signInWithPhoneNumber(pendingPhone, recaptchaVerifier);

    // Update the stored confirmation
    pendingConfirmation = confirmation;

    return { success: true };
  } catch (err) {
    console.error('[firebaseAuth] resendCode error:', err);

    let message = "Couldn't reach Splyt. Check your connection.";
    if (isAuthError(err) && SEND_CODE_ERRORS[err.code]) {
      message = SEND_CODE_ERRORS[err.code];
    }

    const error = new Error(message);
    error.code = err?.code;
    throw error;
  }
}
